import React from 'react';
import { X } from 'lucide-react';

/*
 * Full Terms, Privacy & IP protection modal. Opened from both the Landing
 * "TERMS & POLICIES" button and the in-app footer link. Drafted as a
 * reasonable plain-language document — not a substitute for advice from a
 * NZ solicitor, but a solid public notice that asserts the design rights
 * and clarifies usage for everyday visitors.
 */
export default function TermsModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(31,61,43,0.78)' }}
      onClick={onClose}
      data-testid="terms-modal"
    >
      <div
        className="max-w-3xl w-full max-h-[90vh] flex flex-col"
        style={{ background: 'var(--cream)', border: '3px solid var(--ink)', boxShadow: '8px 8px 0 var(--amber)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '2px solid var(--ink)' }}>
          <h3 className="font-display text-lg tracking-wider" style={{ color: 'var(--ink)' }}>
            TERMS, PRIVACY &amp; DESIGN PROTECTION
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center"
            style={{ background: 'var(--ink)', color: 'var(--cream)' }}
            data-testid="close-terms"
            aria-label="Close"
          ><X size={16} /></button>
        </header>

        <div className="px-6 py-5 overflow-y-auto font-mono text-[12.5px] leading-relaxed space-y-7" style={{ color: 'var(--ink)' }}>
          <p className="opacity-70 text-[11px]">
            Last updated: 2026 · Operator: Heritage Dahlias NZ Limited · Jurisdiction: New Zealand
          </p>

          {/* 1 — TERMS OF USE */}
          <section>
            <h4 className="font-condensed text-base tracking-widest mb-2" style={{ color: 'var(--amber-2)' }}>1. TERMS OF USE</h4>
            <p>By using Cropasaurus Rex (the &ldquo;Service&rdquo;) at cropasaurusrex.nz, you agree to these Terms. The Service is provided as-is by Heritage Dahlias NZ Limited (&ldquo;we&rdquo;, &ldquo;us&rdquo;) for the purpose of locally processing images you supply.</p>
            <ul className="mt-2 space-y-1 list-disc pl-5">
              <li>You retain all rights in the images you process.</li>
              <li>You agree not to use the Service to process material that infringes any third party&apos;s rights or that is unlawful in New Zealand.</li>
              <li>You agree not to attempt to copy, redistribute, mirror, frame, or otherwise re-host the Service or its underlying assets.</li>
              <li>The Service may be modified or withdrawn at any time without notice.</li>
            </ul>
          </section>

          {/* 2 — PRIVACY */}
          <section>
            <h4 className="font-condensed text-base tracking-widest mb-2" style={{ color: 'var(--amber-2)' }}>2. PRIVACY POLICY</h4>
            <p>The Service runs entirely in your browser. We follow the data-minimisation principle of the New Zealand Privacy Act 2020.</p>
            <ul className="mt-2 space-y-1 list-disc pl-5">
              <li>Images you upload never leave your device. There is no upload server.</li>
              <li>Saved Nests (projects) live in your local browser storage (IndexedDB) on your own device.</li>
              <li>We do not set cookies, run analytics, embed third-party tracking pixels, or call third-party APIs.</li>
              <li>We do not collect names, emails, IP addresses, or any personal information.</li>
              <li>The site is hosted on standard web infrastructure which may keep short-lived server logs for security and abuse-prevention purposes. We do not access these for any marketing purpose.</li>
            </ul>
          </section>

          {/* 3 — INTELLECTUAL PROPERTY */}
          <section>
            <h4 className="font-condensed text-base tracking-widest mb-2" style={{ color: 'var(--amber-2)' }}>3. INTELLECTUAL PROPERTY &amp; DESIGN PROTECTION</h4>
            <p>The following are the original creative work and intellectual property of Heritage Dahlias NZ Limited:</p>
            <ul className="mt-2 space-y-1 list-disc pl-5">
              <li>The names <b>Cropasaurus</b>, <b>Cropasaurus Rex</b>, <b>Heritage Crop</b>, <b>Dino Crop</b>, <b>The Herd</b>, <b>Hatchlings</b>, <b>Nests</b>, and the step naming <i>Feed · Chomp · Hatch · Name · Release · Nest</i>.</li>
              <li>The tortoise / sauropod mark in all of its variants (green pill, line-art, favicon, app icon).</li>
              <li>The Heritage Crop scalloped border shape and the Dino Crop silhouette as crop mask designs.</li>
              <li>The visual identity: cream / dark-green / amber palette, zine layout, typography pairing, copywriting voice and tone.</li>
              <li>All source code, layout, microcopy, and product structure.</li>
            </ul>
            <p className="mt-3">Trade mark application(s) are pending with the Intellectual Property Office of New Zealand (IPONZ). &ldquo;Cropasaurus Rex&rdquo;, &ldquo;Heritage Crop&rdquo;, and the tortoise mark are claimed trade marks of Heritage Dahlias NZ Limited.</p>
            <p className="mt-3 font-bold" style={{ color: 'var(--ink)' }}>Unauthorised reproduction, redistribution, framing, scraping, training of AI / ML models on, or derivative works of the Service, its branding, microcopy, layout, or any associated visual or code assets is strictly prohibited and may result in legal action under the Copyright Act 1994 (NZ), the Trade Marks Act 2002 (NZ), and the Fair Trading Act 1986 (NZ).</p>
          </section>

          {/* 4 — DISCLAIMER */}
          <section>
            <h4 className="font-condensed text-base tracking-widest mb-2" style={{ color: 'var(--amber-2)' }}>4. DISCLAIMER &amp; LIABILITY</h4>
            <p>The Service is provided &ldquo;as is&rdquo; without warranty of any kind. To the maximum extent permitted by law, Heritage Dahlias NZ Limited disclaims all warranties, express or implied, including fitness for a particular purpose. You are responsible for backing up the images you process. We are not liable for any direct, indirect, incidental or consequential loss arising from your use of the Service.</p>
            <p className="mt-2">Nothing in these terms limits your rights under the Consumer Guarantees Act 1993 (NZ) where they apply.</p>
          </section>

          {/* 5 — CONTACT */}
          <section>
            <h4 className="font-condensed text-base tracking-widest mb-2" style={{ color: 'var(--amber-2)' }}>5. CONTACT</h4>
            <p>For licensing requests, permissions, takedown notices, privacy enquiries, or anything else:</p>
            <ul className="mt-2 space-y-1 list-disc pl-5">
              <li>Web: <a href="https://heritagedahlias.nz" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--amber-2)' }}>heritagedahlias.nz</a></li>
              <li>Sister site: <a href="https://heritagebloomclub.nz" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--amber-2)' }}>heritagebloomclub.nz</a></li>
              <li>Tool: <a href="https://cropasaurusrex.nz" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--amber-2)' }}>cropasaurusrex.nz</a></li>
            </ul>
          </section>

          <p className="text-[11px] opacity-60 pt-2" style={{ borderTop: '1px solid rgba(31,61,43,0.2)' }}>
            © 2026 Heritage Dahlias NZ Limited. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
